import type { DeckShare } from '@/config/objects/deck-shares';
import { buildDeckShareUrl } from '@/lib/deck-share-url';
import { Button, toast } from '@startupquickstart/frontend';
import { Check, Copy } from 'lucide-react';
import { useCallback, useState } from 'react';

interface DeckShareLinkCellProps {
  share?: DeckShare;
}

/** How long the button shows the "copied" tick before reverting. */
const COPIED_FEEDBACK_MS = 2000;

/**
 * Copy-the-link cell for the shares list. The full URL is deliberately not
 * printed in the row — a 32-character token turns every row into a wall of
 * random characters — so the button is the whole affordance, and the URL is
 * shown in full on the detail page.
 *
 * `stopPropagation` because the row navigates on click: copying a link should
 * not also leave the page.
 */
export function DeckShareLinkCell({ share }: DeckShareLinkCellProps) {
  const [copied, setCopied] = useState(false);
  const shareUrl = share?.shareUrl ?? buildDeckShareUrl(share?.token);

  const handleCopy = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (!shareUrl) return;
      void navigator.clipboard.writeText(shareUrl).then(
        () => {
          setCopied(true);
          toast.success('Presentation link copied');
          window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
        },
        () => toast.error('Could not copy the link'),
      );
    },
    [shareUrl],
  );

  if (!shareUrl) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCopy}
      className="cursor-pointer"
      aria-label={`Copy presentation link for ${share?.recipientName ?? 'this share'}`}
    >
      {copied ? (
        <Check className="h-4 w-4 text-emerald-600" aria-hidden="true" />
      ) : (
        <Copy className="h-4 w-4" aria-hidden="true" />
      )}
      {copied ? 'Copied' : 'Copy link'}
    </Button>
  );
}
